/**
 * The sign-in dialog: "Sign in" in the header and on the landing open it over whatever page is showing
 * (`openSignIn()`); it closes on Escape, a click outside, or once the account is signed in.
 */
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { LOOK } from '@play/ui';
import { useAuth } from './auth';
import { SignInPanel } from './SignInPanel';

const OPEN_EVENT = 'play:sign-in';

/** Opens the sign-in dialog (from a click handler anywhere). */
export function openSignIn(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

/** Mounted once by the app shell. */
export function SignInDialog() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const on = () => {
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, on);
    return () => {
      window.removeEventListener(OPEN_EVENT, on);
    };
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  if (!open || user) return null;
  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: 'rgba(6,9,13,0.72)', backdropFilter: 'blur(4px)' }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="signin-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div className="relative w-full max-w-[420px]">
        <button
          type="button"
          onClick={() => {
            setOpen(false);
          }}
          aria-label="Close"
          className="absolute right-2.5 top-2 z-[1] w-7 h-7 inline-flex items-center justify-center rounded-[7px] hover:bg-white/[0.06]"
          style={{ color: LOOK.sub }}
        >
          <X size={16} />
        </button>
        <SignInPanel autoFocus />
      </div>
    </div>
  );
}
